import { parseCapabilities } from "@/api";

/**
 * Boot-time diagnosability for an unset `VITE_STUDIO_API_CAPABILITIES`.
 *
 * That variable is baked in at BUILD time. When the deploying environment
 * doesn't define it, the build compiles with an empty allow-list, which means
 * every optional group is off: no `/context` (so no View-storefront link, no
 * store currency, a degraded publish preflight), no browse pickers, no image
 * upload, no version history/restore/rollback, no gallery adopt.
 *
 * The failure is invisible by construction — the capability gate refuses each
 * call BEFORE it reaches the network, so there is no failed request in the
 * console and the UI merely renders its honest degraded state. A deployed
 * environment therefore looks "fine but oddly featureless". This warning is
 * the only signal, so it names the variable, the consequences and the fix.
 *
 * Lives here rather than in `src/api/capabilities.ts` because `src/api/` is a
 * SEALED module (consume, don't edit). It reads the same env value through the
 * sealed `parseCapabilities`, so the verdict can never drift from the gate's.
 */
let warned = false;

export function warnIfNoCapabilities(
  raw: string | undefined = import.meta.env.VITE_STUDIO_API_CAPABILITIES as
    | string
    | undefined,
): void {
  if (warned) return;
  // Parsed, not raw-length-checked: a list of typos resolves to nothing
  // enabled, which is the same broken state as an unset variable.
  if (parseCapabilities(raw).length > 0) return;
  warned = true;
  // eslint-disable-next-line no-console
  console.warn(
    "[studio] No optional API capability groups are enabled " +
      "(VITE_STUDIO_API_CAPABILITIES is empty). Image upload, publish " +
      "preflight, version history, adopt and the category/product pickers " +
      "will stay disabled. This is a build-time value — set it in the " +
      "deploying environment's secrets and redeploy.",
  );
}

/** Test seam: forget that the warning already fired. */
export function resetCapabilityWarningForTest(): void {
  warned = false;
}
